import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, cld, errMsg } from "@/lib/api";
import { UrlUpload } from "@/components/admin/UrlUpload";

const BLANK = { name: "", slug: "", description: "", header_image_url: "", display_order: 100 };
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const CategoryForm = ({ editing, onDone }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const isNew = !editing?.id;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    setF(editing?.id ? { ...BLANK, ...editing, header_image_url: editing.header_image_url || "" } : BLANK);
  }, [editing]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, slug: f.slug || null, header_image_url: f.header_image_url || null, display_order: Number(f.display_order) || 100 };
    try {
      if (isNew) await api.post("/admin/categories", body);
      else await api.put(`/admin/categories/${editing.id}`, body);
      toast.success(isNew ? "Category created" : "Category saved");
      setF(BLANK);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 bg-[#FDFBF7] p-6" data-testid="category-form">
      <p className="eyebrow">{isNew ? "New category" : `Edit — ${editing.name}`}</p>
      <label className="block"><span className="field-label mb-1">Name</span><input required className="admin-input" value={f.name} onChange={(e) => { set("name", e.target.value); if (isNew) set("slug", slugify(e.target.value)); }} data-testid="category-form-name" /></label>
      <label className="block"><span className="field-label mb-1">URL — /category/{f.slug || "…"}</span><input className="admin-input" value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="category-form-slug" /></label>
      <label className="block"><span className="field-label mb-1">Description</span><textarea rows={3} className="admin-input" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="category-form-description" /></label>
      <UrlUpload label="Header image" value={f.header_image_url} onChange={(v) => set("header_image_url", v)} folder="art-store/categories" testid="category-form-header" />
      <label className="block"><span className="field-label mb-1">Display order (lower shows first)</span><input type="number" className="admin-input" value={f.display_order} onChange={(e) => set("display_order", e.target.value)} data-testid="category-form-order" /></label>
      <div className="flex gap-3">
        <button className="btn-primary px-6 py-3" disabled={busy} data-testid="category-form-save">{busy ? "Saving…" : isNew ? "Create category" : "Save"}</button>
        {!isNew && <button type="button" className="btn-outline px-6 py-3" onClick={() => onDone()} data-testid="category-form-cancel">Cancel</button>}
      </div>
    </form>
  );
};

export const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/categories").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const del = async (c) => {
    if (!window.confirm(`Delete the "${c.name}" category? Artworks stay in the store, they're just removed from this category.`)) return;
    await api.delete(`/admin/categories/${c.id}`).then(() => { toast.success("Category deleted"); load(); }).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="admin-categories">
      <div className="bg-[#FDFBF7] p-6 lg:col-span-7">
        <p className="eyebrow mb-6">{items.length} {items.length === 1 ? "category" : "categories"}</p>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E5E0D8] text-left text-[10px] uppercase tracking-[0.2em] text-[#5A626A]"><th className="py-3">Category</th><th>Works</th><th>Order</th><th /></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="py-6 text-[#8A8780]">No categories yet</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-[#F0EDE6]" data-testid={`category-row-${c.slug}`}>
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    {c.header_image_url && <img src={cld(c.header_image_url, "w_96,q_auto,f_auto")} alt="" className="h-10 w-14 object-cover" />}
                    <div><p className="font-medium">{c.name}</p><a href={`/category/${c.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#8A8780] underline underline-offset-2">/category/{c.slug}</a></div>
                  </div>
                </td>
                <td>{c.productCount}</td>
                <td>{c.display_order}</td>
                <td className="whitespace-nowrap text-right">
                  <button className="mr-4 text-xs underline underline-offset-4" onClick={() => setEditing(c)} data-testid={`category-edit-btn-${c.slug}`}>Edit</button>
                  <button className="text-xs text-[#8B2E24] underline underline-offset-4" onClick={() => del(c)} data-testid={`category-delete-btn-${c.slug}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-6 text-xs text-[#8A8780]">Assign artworks to categories with the checkboxes in each artwork's Edit form.</p>
      </div>
      <div className="lg:col-span-5"><CategoryForm editing={editing} onDone={() => { setEditing(null); load(); }} /></div>
    </div>
  );
};
